/**
 * GDD section 2: during placement, the valid zones are highlighted in green, the others in red. Every top surface
 * in the view (a cell that is not empty or gas, under one that is) is marked with what putting an invader there,
 * centred on its column, would give. placementSpot is not free: computed again a few times a second, or when the
 * view or the units change.
 */
import { MATERIALS, STATE_GAS } from '@invaders/sim';
import { placementSpot, UNIT_H, type GameState } from '@invaders/game';
import type { Camera } from '../shared/camera';

const REFRESH_MS = 250;
const OPEN = Uint8Array.from(MATERIALS, (m, id) => (id === 0 || m.state === STATE_GAS ? 1 : 0));

export class PlacementZones {
  /** Flat [x, y, ok, ...]: the surface cells of the view. */
  private marks: number[] = [];
  private at = -Infinity;
  private key = '';

  /** `distance`: to the enemies, as the game asks it now (placementDistance). */
  update(state: GameState, player: number, cam: Camera, now: number, distance: number): void {
    const key = `${player} ${state.units.length} ${distance} ${Math.floor(cam.x)} ${Math.floor(cam.y)} ${Math.floor(cam.spanW)}`;
    if (key === this.key && now - this.at < REFRESH_MS) return;
    this.key = key;
    this.at = now;
    this.marks = [];
    const { w, h, data } = state.world.physique;
    const y0 = Math.max(1, Math.floor(cam.y));
    const y1 = Math.min(h - 1, Math.ceil(cam.y + cam.spanH));
    for (let x = Math.max(0, Math.floor(cam.x)); x < Math.min(w, Math.ceil(cam.x + cam.spanW)); x++) {
      for (let y = y0; y <= y1; y++) {
        if (OPEN[data[y * w + x]] === 1 || OPEN[data[(y - 1) * w + x]] === 0) continue;
        // Dropped from just over this surface, does it land here, and is it allowed?
        const spot = placementSpot(state, player, x, y - (UNIT_H >> 1) - 1, distance);
        this.marks.push(x, y, spot.ok && spot.y + UNIT_H === y ? 1 : 0);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, cam: Camera): void {
    const s = cam.scale;
    const m = this.marks;
    for (let i = 0; i < m.length; i += 3) {
      ctx.fillStyle = m[i + 2] === 1 ? 'rgba(95, 211, 95, 0.9)' : 'rgba(255, 70, 70, 0.75)';
      ctx.fillRect((m[i] - cam.x) * s, (m[i + 1] - cam.y) * s, s, s);
    }
  }
}
